import React from "react";
import DrawerHeader from "@/components/DrawerHeader";
import FullScreenDrawer from "@/components/FullScreenDrawer";
import theme from "@/theme";
import { useMediaQuery, CircularProgress } from "@mui/material";
import { DrawerPageProps } from "./types";
import { DroppsProduct } from "@/store";
import { ShopifyCartItem } from "@/services/shopifyCart";
import { useIsMutating } from "@tanstack/react-query";

export type DrawerPageRenderer = (props: DrawerPageProps) => React.ReactNode;

export type Page =
  | { kind: "overview" }
  | { kind: "choose-gift" }
  | {
      kind: "bundle-drawer";
      bundledProducts: DroppsProduct[];
      item: ShopifyCartItem;
    };

export type CartDrawerPageProps = DrawerPageProps & {
  page: Page;
};

export type CartDrawerProps = DrawerPageProps & {
  contents?: DrawerPageRenderer;
  open?: boolean;
  transparentBackdrop?: boolean;
  title?: string;
};

export const CartDrawer = ({
  contents,
  open,
  onExit: origExit,
  onBack,
  goToFirstPage: originalGoToFirstPage,
  transparentBackdrop = false,
  title = "",
}: CartDrawerProps) => {
  const isLoading = !!useIsMutating();
  const fullScreen: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  const onExit = () => {
    onBack?.();
    origExit();
  };

  const goToFirstPage = () => {
    onBack?.();
    originalGoToFirstPage();
  };

  return (
    <FullScreenDrawer
      anchor="right"
      open={open}
      onClose={onExit}
      fullscreen={fullScreen ? "true" : undefined}
      role="dialog"
      aria-modal={true}
      aria-label={title ?? "Your cart"}
      BackdropProps={transparentBackdrop ? { className: "bg-transparent" } : {}}
    >
      <div className="relative grow">
        <DrawerHeader
          sticky={true}
          handleClose={onExit}
          handleBack={onBack}
          text={title ?? "Your cart"}
        />
        {contents?.({ onExit, onBack, goToFirstPage }) ?? null}
        {isLoading && (
          <>
            <div className="absolute left-0 top-0 z-30 h-full w-full cursor-wait bg-white opacity-50 transition"></div>
            <div className="absolute left-0 top-0 z-30 flex h-full w-full cursor-wait items-center justify-center">
              <CircularProgress />
            </div>
          </>
        )}
      </div>
    </FullScreenDrawer>
  );
};
